import axios from './axios';

/**
 * Get all TTL files
 */
export const loadFiles = async () => {
  const response = await axios.get('files/');
  return response.data;
};

/**
 * Upload TTL file
 */
export const uploadFile = async (formData, onUploadProgress) => {
  const fileName = formData.get('file')?.name ?? '(unknown)';
  const configId = formData.get('translation_config_id') ?? null;
  console.log('[fileApi] 📡 POST files/upload/ —', fileName, configId ? `config=${configId}` : 'no config');

  const response = await axios.post('files/upload/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000, // 2 minutes for file uploads
    onUploadProgress: (evt) => {
      if (onUploadProgress) onUploadProgress(evt);
    }
  });

  console.log('[fileApi] ✅ Response', response.status, '—', response.data?.message ?? JSON.stringify(response.data));
  return response.data;
};

/**
 * Upload a document to ingestion pipeline without linking it to a project.
 */
export const uploadStandaloneDocument = async (file, onUploadProgress) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post('ingestion/upload/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
    onUploadProgress,
  });

  return response.data;
};

/**
 * Poll progress of ingestion/upload task.
 */
export const getIngestionUploadProgress = async (taskId) => {
  const response = await axios.get(`ingestion/upload-progress/${encodeURIComponent(taskId)}/`);
  return response.data;
};

/**
 * List ingestion documents (project-linked and standalone).
 */
export const listIngestionDocuments = async () => {
  const response = await axios.get('ingestion/documents/');
  return response.data;
};

/**
 * Get ingestion document file as blob for inline preview.
 */
export const viewIngestionDocument = async (documentId) => {
  const response = await axios.get(`ingestion/documents/${documentId}/view/`, {
    responseType: 'blob',
  });
  return response.data;
};

/**
 * Queue document reprocessing.
 */
export const reloadIngestionDocument = async (documentId) => {
  const response = await axios.post(`ingestion/documents/${documentId}/reload/`);
  return response.data;
};

/**
 * Delete an ingestion document.
 */
export const deleteIngestionDocument = async (documentId) => {
  const response = await axios.delete(`ingestion/documents/${documentId}/delete/`);
  return response.data;
};

/**
 * Get a previously generated knowledge graph for an ingestion document.
 */
export const getIngestionDocumentGraph = async (documentId) => {
  const response = await axios.get(`ingestion/documents/${documentId}/graph/`);
  return response.data;
};

/**
 * Generate (or regenerate) the knowledge graph for an ingestion document.
 */
export const generateIngestionDocumentGraph = async (documentId, force = true, pipeline = 'new') => {
  const response = await axios.post(`ingestion/documents/${documentId}/graph/`, { force, pipeline });
  return response.data;
};

/**
 * Get LangExtract graph visualization HTML for an ingestion document.
 */
export const viewIngestionDocumentGraphVisualization = async (documentId) => {
  const response = await axios.get(`ingestion/documents/${documentId}/graph-visualization/`, {
    responseType: 'blob',
  });
  return response.data;
};

/**
 * Delete file
 */
export const deleteFile = async (fileId) => {
  const response = await axios.delete(`files/delete/${fileId}/`);
  return response.data;
};

/**
 * Download Excel file
 */
export const downloadExcel = async (fileId, confirm = false) => {
  const url = confirm 
    ? `files/${fileId}/export-excel/?confirm=true`
    : `files/${fileId}/export-excel/`;
  
  const response = await axios.get(url, {
    responseType: confirm ? 'blob' : 'json'
  });
  
  if (confirm && response.data) {
    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `requirements_${fileId}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  }
  
  return response.data;
};

/**
 * File management API service
 */
export const fileApi = {
  getFiles: loadFiles,
  uploadFile,
  uploadStandaloneDocument,
  getIngestionUploadProgress,
  listIngestionDocuments,
  viewIngestionDocument,
  reloadIngestionDocument,
  deleteIngestionDocument,
  getIngestionDocumentGraph,
  generateIngestionDocumentGraph,
  viewIngestionDocumentGraphVisualization,
  deleteFile,
  downloadExcel
};

export default fileApi;
